/**
 * White-glove onboarding: setup fee, token validation, intake validation.
 * Plan: billing_infrastructure_plan §1.8. Fee: $500 base + $3 × (systems over 100).
 * Volume bands: 1-99: 0%, 100-249: 15%, 250-999: 20%, 1000+: 25%.
 */

const VOLUME_BANDS = [
  { min: 1, max: 99, discountPct: 0 },
  { min: 100, max: 249, discountPct: 15 },
  { min: 250, max: 999, discountPct: 20 },
  { min: 1000, max: Infinity, discountPct: 25 },
] as const;

/** White-glove one-time setup fee (USD). $500 base + $3 per system over 100. */
export function whiteGloveSetupFee(systemCount: number): number {
  if (systemCount < 0) return 0;
  const over100 = Math.max(0, systemCount - 100);
  return 500 + 3 * over100;
}

/** Alert tier monthly before volume discount (USD). $7/system. */
export function whiteGloveMonthlyAlert(systemCount: number): number {
  if (systemCount < 0) return 0;
  return systemCount * 7;
}

/** Volume discount % for display (e.g. "15% off at next invoice"). Same bands as billing. */
export function whiteGloveVolumeDiscountPct(systemCount: number): number {
  for (const band of VOLUME_BANDS) {
    if (systemCount >= band.min && systemCount <= band.max) return band.discountPct;
  }
  return 0;
}

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuid(token: string): boolean {
  return UUID_REGEX.test(token);
}

export function isTokenExpired(expiresAt: string | Date): boolean {
  const t = typeof expiresAt === 'string' ? new Date(expiresAt).getTime() : expiresAt.getTime();
  return Number.isNaN(t) || t < Date.now();
}

/** Supported platforms for white-glove credential form. */
export const WHITE_GLOVE_PLATFORMS = [
  { id: 'solark', label: 'Sol-Ark' },
  { id: 'eg4', label: 'EG4' },
  { id: 'franklinwh', label: 'FranklinWH' },
  { id: 'solaredge', label: 'SolarEdge' },
  { id: 'enphase', label: 'Enphase' },
  { id: 'tesla', label: 'Tesla Powerwall' },
] as const;

export type WhiteGlovePlatformId = (typeof WHITE_GLOVE_PLATFORMS)[number]['id'];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface WhiteGloveIntakeData {
  estimatedSystemCount: number;
  platforms: string[];
  email: string;
  phone: string;
}

export interface WhiteGloveIntakeErrors {
  estimatedSystemCount?: string;
  platforms?: string;
  email?: string;
  phone?: string;
}

export function validateWhiteGloveIntake(data: WhiteGloveIntakeData): WhiteGloveIntakeErrors {
  const errors: WhiteGloveIntakeErrors = {};
  if (data.estimatedSystemCount == null || data.estimatedSystemCount < 1) {
    errors.estimatedSystemCount = 'System count is required and must be greater than 0';
  }
  if (!Array.isArray(data.platforms) || data.platforms.length === 0) {
    errors.platforms = 'Select at least one platform';
  }
  if (!data.email?.trim()) {
    errors.email = 'Email is required';
  } else if (!EMAIL_REGEX.test(data.email.trim())) {
    errors.email = 'Enter a valid email address';
  }
  return errors;
}

export function validateWhiteGloveCredentials(
  platforms: string[],
  credentials: Record<string, { username: string; password: string }>
): string | null {
  for (const platform of platforms) {
    const c = credentials[platform];
    if (!c?.username?.trim() || !c?.password?.trim()) {
      return 'All platforms require credentials';
    }
  }
  return null;
}
